import { useState } from 'react';
import { api, ApiError, type Track } from '../../api.js';
import ConfirmDialog from '../../components/ConfirmDialog.js';
import RepoBadge from '../../components/RepoBadge.js';
import PipelineStepper from './PipelineStepper.js';
import { isActionable, reviewFlags, stageFor } from './statusMap.js';
import { useTimeInState } from './useTimeInState.js';

interface CiIndeterminateInfo {
  detail: string;
  ci_lookup_status: number | null;
  ci_lookup_message: string | null;
}

async function mergeTrack(id: string, acknowledgeUnverifiedCi?: boolean): Promise<void> {
  const res = await fetch(`/api/tracks/${id}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(acknowledgeUnverifiedCi ? { acknowledge_unverified_ci: true } : {}),
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? body?.detail ?? `request failed: ${res.status}`,
      body?.blocked,
      body
    );
  }
}

function ActionStripRow({
  track,
  recentCompleted,
  onOpen,
  refreshList,
}: {
  track: Track;
  recentCompleted: Track[];
  onOpen: (id: string) => void;
  refreshList: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ciConfirm, setCiConfirm] = useState<CiIndeterminateInfo | null>(null);
  const { label, slow } = useTimeInState(track, recentCompleted);
  const { stage, stageState } = stageFor(track);
  const flags = reviewFlags(track);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refreshList();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const buttons: React.ReactNode[] = [];
  if (track.state === 'SPEC_REVIEW') {
    buttons.push(
      <button
        key="approve"
        type="button"
        className="action-strip-btn"
        disabled={busy}
        onClick={() => act(() => api.approve(track.id))}
      >
        Approve &amp; build
      </button>,
      <button
        key="revise"
        type="button"
        className="action-strip-btn action-strip-btn-secondary"
        disabled={busy}
        onClick={() => act(() => api.revise(track.id))}
      >
        Revise
      </button>
    );
  } else if (track.state === 'PR_OPEN') {
    buttons.push(
      <button
        key="merge"
        type="button"
        className="action-strip-btn"
        disabled={busy}
        onClick={() =>
          act(async () => {
            try {
              await mergeTrack(track.id);
            } catch (e) {
              if (e instanceof ApiError && e.code === 'ci_status_indeterminate') {
                const details = (e.details ?? {}) as { ci_lookup_status?: number | null; ci_lookup_message?: string | null };
                setCiConfirm({
                  detail: e.message,
                  ci_lookup_status: details.ci_lookup_status ?? null,
                  ci_lookup_message: details.ci_lookup_message ?? null,
                });
                return;
              }
              throw e;
            }
          })
        }
      >
        Review &amp; merge
      </button>
    );
    if (track.pr_url) {
      buttons.push(
        <a
          key="pr"
          className="action-strip-btn action-strip-btn-secondary"
          href={track.pr_url}
          target="_blank"
          rel="noreferrer"
        >
          View PR #{track.pr_number ?? ''}
        </a>
      );
    }
  } else if (track.state === 'AWAITING_PREREQS') {
    buttons.push(
      <button
        key="prereqs"
        type="button"
        className="action-strip-btn"
        disabled={busy}
        onClick={() => act(() => api.prereqsDone(track.id))}
      >
        I&apos;ve done the prereqs
      </button>
    );
  } else if (track.state === 'BLOCKED_HUMAN') {
    buttons.push(
      <button
        key="open"
        type="button"
        className="action-strip-btn action-strip-btn-secondary"
        onClick={() => onOpen(track.id)}
      >
        Open
      </button>
    );
  }

  return (
    <div className="action-strip-row">
      <div className="action-strip-row-top">
        <span className="action-strip-chip" />
        <button type="button" className="action-strip-title" onClick={() => onOpen(track.id)}>
          {track.title}
        </button>
      </div>
      <div className="action-strip-chip-row">
        <RepoBadge targetRepo={track.spec.target_repo} />
        {flags.map((f) => (
          <span key={f.key} className="review-flag-chip" data-verdict={f.verdict}>
            {f.label}
          </span>
        ))}
      </div>
      {error && <div className="action-strip-row-error">{error}</div>}
      {ciConfirm && (
        <ConfirmDialog
          title="CI status could not be verified"
          message={
            <>
              <p>{ciConfirm.detail}</p>
              {ciConfirm.ci_lookup_status !== null && (
                <p>
                  GitHub returned status {ciConfirm.ci_lookup_status}
                  {ciConfirm.ci_lookup_message ? `: ${ciConfirm.ci_lookup_message}` : ''}.
                </p>
              )}
              <p>Proceeding merges this PR without automated CI verification.</p>
            </>
          }
          confirmLabel="Merge without verified CI"
          danger
          busy={busy}
          onCancel={() => setCiConfirm(null)}
          onConfirm={() =>
            act(async () => {
              setCiConfirm(null);
              await mergeTrack(track.id, true);
            })
          }
        />
      )}
      <PipelineStepper
        stage={stage}
        stageState={stageState}
        timeLabel={label}
        slow={slow}
        className="action-strip-stepper"
      />
      <div className="action-strip-actions">{buttons}</div>
    </div>
  );
}

export default function ActionStrip({
  tracks,
  recentCompleted,
  onOpen,
  refreshList,
}: {
  tracks: Track[];
  recentCompleted: Track[];
  onOpen: (id: string) => void;
  refreshList: () => void;
}) {
  const actionable = tracks.filter((t) => isActionable(t.state));
  if (actionable.length === 0) return null;

  return (
    <div className="action-strip">
      <div className="action-strip-header">
        <span className="action-strip-header-title">⚡ Needs your action</span>
        <span className="action-strip-badge">{actionable.length}</span>
      </div>
      <div className="action-strip-rows">
        {actionable.map((track) => (
          <ActionStripRow
            key={track.id}
            track={track}
            recentCompleted={recentCompleted}
            onOpen={onOpen}
            refreshList={refreshList}
          />
        ))}
      </div>
    </div>
  );
}
